import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import {
  approveTransfer,
  employeeLabelOf,
  listEmployees,
  listTerritories,
  listTransfers,
  rejectTransfer,
  type EmployeeRow,
  type TerritoryRow,
  type TransferRow,
} from '../../api/orgApi.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

export function TransferPage(): ReactNode {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TransferRow[]>([]);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [territories, setTerritories] = useState<TerritoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    listEmployees(token).then(r => setEmployees(r.data)).catch(() => undefined);
    listTerritories(token).then(r => setTerritories(r.data)).catch(() => undefined);
  }, [token]);

  useEffect(() => {
    setLoading(true);
    listTransfers(token)
      .then(r => setRows(r.data))
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  function reload(): void {
    listTransfers(token).then(r => setRows(r.data)).catch(e => setError((e as Error).message));
  }

  const empName = useMemo(() => Object.fromEntries(employees.map(e => [e.empExtId, employeeLabelOf(e)])), [employees]);
  const terrCode = useMemo(() => Object.fromEntries(territories.map(t => [t.territoryId, t.territoryCode])), [territories]);
  const terrOf = (id?: string | null): string => (id ? (terrCode[id] ?? '—') : '—');

  async function approve(id: string): Promise<void> {
    setBusyId(id); setError(null);
    try { await approveTransfer(token, id); setNotice(t('org.transfer.approved')); reload(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusyId(null); }
  }

  async function reject(id: string): Promise<void> {
    const reason = window.prompt(t('org.transfer.rejectPrompt'));
    if (reason == null) return;
    setBusyId(id); setError(null);
    try { await rejectTransfer(token, id, reason || 'Rejected'); setNotice(t('org.transfer.rejected')); reload(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusyId(null); }
  }

  const statusBg = (s?: string): string =>
    s === 'APPROVED' ? 'var(--color-success-bg)' : s === 'REJECTED' ? 'var(--color-danger-bg)' : 'var(--color-warning-bg)';

  return (
    <main aria-label="transfer-list" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1>{t('org.transfer.title')}</h1>
        <Link to="/org/transfers/new" style={{ padding: '8px 16px', borderRadius: 6, background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)', textDecoration: 'none' }}>{t('org.transfer.new')}</Link>
      </div>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {notice && <p style={{ color: 'var(--color-success)' }}>{notice}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && rows.length === 0 && <p>{t('org.transfer.empty')}</p>}
      {rows.length > 0 && (
        <DataTable<TransferRow>
          rowKey={(row) => row.transferId}
          rows={rows}
          columns={[
            { key: 'employee', header: t('org.col.employee'), render: (row) => empName[row.employeeId] ?? row.employeeId },
            { key: 'type', header: t('org.col.type'), render: (row) => row.transferType },
            { key: 'from', header: t('org.col.from'), render: (row) => <span style={{ fontFamily: 'monospace' }}>{terrOf(row.fromTerritoryId)}</span> },
            { key: 'to', header: t('org.col.to'), render: (row) => <span style={{ fontFamily: 'monospace' }}>{terrOf(row.toTerritoryId)}</span> },
            { key: 'effectiveDate', header: t('org.col.effectiveDate'), render: (row) => day(row.effectiveDate) },
            { key: 'status', header: t('org.col.status'), render: (row) => <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: statusBg(row.approvalStatus) }}>{row.approvalStatus ?? 'PENDING'}</span> },
            { key: 'actions', header: t('org.col.actions'), render: (row) => (row.approvalStatus ?? 'PENDING') === 'PENDING' ? (
              <>
                <Button type="button" variant="primary" onClick={() => approve(row.transferId)} disabled={busyId === row.transferId} style={{ marginRight: 8 }}>{t('org.transfer.approve')}</Button>
                <Button type="button" variant="danger" onClick={() => reject(row.transferId)} disabled={busyId === row.transferId}>{t('org.transfer.reject')}</Button>
              </>
            ) : <span style={{ color: 'var(--color-text-secondary)' }}>—</span> },
          ]}
        />
      )}
    </main>
  );
}
